import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useMutation, useQuery } from "@apollo/client";
import { Box, Button } from "@mui/material";
import { MyHrContractCard } from "my-hr/components";
import { useMyHrContract, useMyHrEmployee } from "my-hr/hooks";
import {
  MyTimesheetPageTitle,
  MyTimesheetShowRequestDialogData,
  MyTimesheetShowRequestDialogMessages,
  MyTimesheetShowRequestDialogStateData,
  MyTimesheetShowRequestDialogTimeData,
  MyTimesheetUserRequestSummaryCard,
} from "my-timesheet/components";
import {
  MY_TIMESHEET_APPROVE_TIMESHEET,
  MY_TIMESHEET_GET_SELECTED_REQUEST,
  MY_TIMESHEET_GET_SUMMARIES_BY_CONTRACT,
  MY_TIMESHEET_REJECT_TIMESHEET,
} from "my-timesheet/graphql";
import { useMySheetCurrentRoleState } from "my-timesheet/hooks";
import { OcCard, OcCardButtonBar, OcPageTransition } from "oc/components";
import { useOcSnackbar, useOcCurrentUserState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

const useMySheetUserRequestData = ({ _id }: { _id: string }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setOpenMessage } = useOcSnackbar();

  const { data, loading, error } = useQuery(MY_TIMESHEET_GET_SELECTED_REQUEST, {
    fetchPolicy: "cache-and-network",
    skip: !_id,
    variables: {
      _id,
    },
  });

  const [approveMyRequest] = useMutation(MY_TIMESHEET_APPROVE_TIMESHEET, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_TIMESHEET_SUCCESSFUL_CREATED"),
      });
      history.goBack();
    },
    onError: (error) => {
      console.log({ error });
      setOpenMessage({
        type: "error",
        message: t("MY_TIMESHEET_CREATE_FAILED"),
      });
    },
  });

  const [rejectMyRequest] = useMutation(MY_TIMESHEET_REJECT_TIMESHEET, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_TIMESHEET_SUCCESSFUL_REJECTED"),
      });
      history.goBack();
    },
    onError: (error) => {
      console.log({ error });
      setOpenMessage({
        type: "error",
        message: t("MY_TIMESHEET_REJECT_FAILED"),
      });
    },
  });

  const request = { ...data?.mySheetTimesheet };

  const { data: contractData } = useMyHrContract({
    contract: request?.contractId,
  });

  const { data: employeeData } = useMyHrEmployee({
    employeeId: request?.createdBy,
  });

  const { data: timesheetSummariesData } = useQuery(
    MY_TIMESHEET_GET_SUMMARIES_BY_CONTRACT,
    {
      fetchPolicy: "cache-and-network",
      skip: !request?.contractId,
      variables: {
        contract: request?.contractId,
      },
    },
  );

  const timesheetSummaries =
    timesheetSummariesData?.timesheetsSummariesByContract || {
      weeklyDuration: "00:00",
      monthlyDuration: "00:00",
      weeklyCost: 0,
      monthlyCost: 0,
    };

  return {
    data: request,
    loading,
    error,
    contractData,
    employeeData,
    timesheetSummaries,
    approveMyRequest,
    rejectMyRequest,
  };
};

export const MyTimesheetUserRequestPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: _id } = useParams<{ id: string }>();
  const isMobile = useOcIsMobile();
  let { contractCode } = useOcCurrentUserState();

  const { state: currentRole } = useMySheetCurrentRoleState();
  const isExaminer = currentRole === "examiner";

  const {
    data: request,
    loading,
    error,
    contractData,
    employeeData,
    timesheetSummaries,
    approveMyRequest,
    rejectMyRequest,
  } = useMySheetUserRequestData({ _id });

  if (request && request?.startTime) {
    let duration = "00:00";
    if (request?.startTime && request?.endTime) {
      duration = moment
        .utc(
          new Date(request?.endTime).getTime() -
          new Date(request?.startTime).getTime(),
        )
        .format("HH:mm");
    }
    request.rawDate = moment(request.startTime).format("YYYY.MM.DD");
    request.rawEndDate = moment(request.endTime).format("YYYY.MM.DD");
    request.startTime = moment(request.startTime).format("HH:mm");
    request.endTime = moment(request.endTime).format("HH:mm");
    request.duration = duration;
  }

  const HUF = new Intl.NumberFormat("hu-HU", {
    style: "currency",
    currency: "HUF",
    maximumSignificantDigits: 3,
  });

  const getTimeSummary = (value: string) => {
    if (!value || !value.includes(":"))
      return `${t("0")} ${t("OC_HOUR").toLowerCase()}`;
    const [hours, minutes] = value.split(":");
    if (Number.parseInt(hours, 10) === 0 && Number.parseInt(minutes, 10) === 0) {
      return `${t("0")} ${t("OC_HOUR").toLowerCase()}`;
    }
    if (Number.parseInt(minutes, 10) === 0) {
      return `${hours} ${t("OC_HOUR").toLowerCase()}`;
    }
    return `${hours} ${t("OC_HOUR").toLowerCase()} ${minutes} ${t(
      "MY_TIMESHEET_MINUTE",
    ).toLowerCase()}`;
  };

  const handleApprove = () => {
    approveMyRequest({
      variables: {
        _id: request?._id,
      },
    });
  };

  const handleReject = () => {
    rejectMyRequest({
      variables: {
        _id: request?._id,
      },
    });
  };

  const handleCancel = () => {
    history.goBack();
  };

  const disabled = request?.contractId === contractCode;

  return (
    <OcPageTransition>
      <Box padding={isMobile ? "0px" : "16px"}>
        <MyTimesheetPageTitle
          title={isExaminer ? t("OC_APPROVE_REQUESTS") : t("OC_REQUESTS_VIEW")}
          showDateRange={false}
          goBack={true}
          isMobile={isMobile}
          handlePeriodChange={() => { }}
        />
        <Box height="16px" />
        <MyHrContractCard employee={employeeData} contract={contractData} />
        <Box height="16px" />
        <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
          <Box width={isMobile ? "100%" : "600px"}>
            <OcCard
              label={t("MY_TIMESHEET_ACTIVITY_REQUEST")}
              loading={loading}
              error={!!error}
            >
              <Box id="my-sheet-show-request-dialog" padding="0px">
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <MyTimesheetShowRequestDialogData
                    request={request}
                    width={isMobile ? "auto" : "calc(100% - 246px)"}
                  />
                  <MyTimesheetShowRequestDialogStateData
                    request={request}
                    isMobile={isMobile}
                  />
                </Box>
                <Box height="16px" />
                <MyTimesheetShowRequestDialogTimeData
                  request={request}
                  isMobile={isMobile}
                />
                <Box height="16px" />
                <MyTimesheetShowRequestDialogMessages request={request} />
              </Box>
              {isExaminer && request?.endTime !== "Invalid date" ? (
                <OcCardButtonBar
                  leftButtonText={t("OC_REJECT")}
                  leftButtonColor={
                    request?.state !== "rejected" ? "primary" : undefined
                  }
                  leftButtonDisabled={loading || disabled}
                  handleLeftButton={handleReject}
                  leftButtonId="request-reject"
                  rightButtonText={t("OC_CANCEL")}
                  handleRightButton={handleCancel}
                  rightButtonId="request-cancel"
                  rightButtonColor="secondary"
                  handlePrimaryButton={handleApprove}
                  primaryButtonText={
                    request?.state === "requested" && t("OC_APPROVE")
                  }
                  primaryButtonDisabled={
                    loading || request?.state === "rejected" || disabled
                  }
                  primaryButtonId="request-approve"
                />
              ) : (
                <Box>
                  <Box
                    paddingTop="32px"
                    style={{ borderBottom: "1px solid #C6CACC" }}
                  />
                  <Box height="16px" />
                  <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    justifyContent="flex-end"
                  >
                    <Button
                      color="secondary"
                      variant="text"
                      onClick={handleCancel}
                    >
                      {t("OC_CANCEL")}
                    </Button>
                  </Box>
                </Box>
              )}
            </OcCard>
          </Box>
          <Box width="16px" />
          {isMobile && <Box height="16px" />}
          <Box>
            <MyTimesheetUserRequestSummaryCard
              weeklyTimeLabel={"Összes heti idő"}
              weeklyTimeValue={getTimeSummary(
                timesheetSummaries?.weeklyDuration,
              )}
              monthlyTimeLabel={"Összes havi idő"}
              monthlyTimeValue={getTimeSummary(
                timesheetSummaries?.monthlyDuration,
              )}
              isMobile={isMobile}
            />
            <Box height="16px" />
            <MyTimesheetUserRequestSummaryCard
              weeklyTimeLabel={"Összes heti idő (számlázható)"}
              weeklyTimeValue={getTimeSummary(
                timesheetSummaries?.weeklyDurationBillable,
              )}
              monthlyTimeLabel={"Összes havi idő (számlázható)"}
              monthlyTimeValue={getTimeSummary(
                timesheetSummaries?.monthlyDurationBillable,
              )}
              isMobile={isMobile}
            />
            <Box height="16px" />
            <MyTimesheetUserRequestSummaryCard
              weeklyTimeLabel={"Összes heti költség"}
              weeklyTimeValue={HUF.format(timesheetSummaries?.weeklyCost)}
              monthlyTimeLabel={"Összes havi költség"}
              monthlyTimeValue={HUF.format(timesheetSummaries?.monthlyCost)}
              isMobile={isMobile}
            />
          </Box>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyTimesheetUserRequestPage;
